import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { AxiosError } from 'axios'
import {
  fetchLabourRates,
  fetchRecipeLabour,
  saveLabourRates,
  saveRecipeLabour,
} from '../api/calculatorApi'
import type {
  LabourItem,
  LabourRate,
  LabourRatesResponse,
  RecipeLabourResponse,
} from '../types'
import { CALCULATOR_KEYS } from './calculatorKeys'

/** Ставки меняются редко — держим их у себя всю сессию. */
export const useLabourRatesQuery = (enabled = true) =>
  useQuery<LabourRatesResponse, AxiosError>(
    CALCULATOR_KEYS.labourRates,
    fetchLabourRates,
    {
      enabled,
      staleTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

type RateInput = Pick<LabourRate, 'role_id' | 'rate'>

/**
 * Сохранение ставок. Ручка отвечает без тела и заменяет весь набор, поэтому
 * кладём в кеш то же, что отправили: перезапрашивать нечего.
 */
export const useSaveLabourRatesMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, readonly RateInput[]>(
    rates => saveLabourRates(rates),
    {
      retry: false,
      onSuccess: (_data, rates) => {
        queryClient.setQueryData<LabourRatesResponse>(
          CALCULATOR_KEYS.labourRates,
          {
            rates: rates.map(rate => ({ ...rate })),
          }
        )
      },
    }
  )
}

/**
 * Часы по рецепту. Принадлежат сохранённому рецепту: у карточки, которой нет в
 * «Моих рецептах», ручка отвечает 404 — это штатный ответ, а не сбой.
 */
export const useRecipeLabourQuery = (cardId: number | null) =>
  useQuery<RecipeLabourResponse, AxiosError>(
    CALCULATOR_KEYS.recipeLabour(cardId ?? 0),
    () => fetchRecipeLabour(cardId as number),
    {
      enabled: cardId !== null,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

interface SaveRecipeLabourInput {
  cardId: number
  items: readonly LabourItem[]
}

/** Часы тоже заменяются целиком: не присланная роль из расчёта убирается. */
export const useSaveRecipeLabourMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError, SaveRecipeLabourInput>(
    ({ cardId, items }) => saveRecipeLabour(cardId, items),
    {
      retry: false,
      onSuccess: (_data, { cardId, items }) => {
        queryClient.setQueryData<RecipeLabourResponse>(
          CALCULATOR_KEYS.recipeLabour(cardId),
          { items: items.map(item => ({ ...item })) }
        )
      },
    }
  )
}
